import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useCallback, useMemo } from "react";
import {
    ExtractionStatus,
    FinancialDocumentListItem,
} from "@common/types/financialDocument";
import { DocumentCategory } from "@common/types/documentCategory";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { Picker } from "@design-system/Picker/Picker.lib";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { ClockIcon, DottedCircleIcon } from "@icons";
import { useUpdateDocumentCategoryMutation } from "@mutations/financialDocument";
import { HStack } from "@design-system/HStack/HStack";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { DocumentCategoryIcon } from "../../DocumentCategoryIcon";
import { DocumentCategoryCombobox } from "../../DocumentCategorySelect/DocumentCategoryCombobox";
import styles from "../FinancialDocumentsView.module.scss";
import { useIsFinancialDocumentsTableReadonly } from "../useIsFinancialDocumentsTableReadonly";

export const DocumentCategoryCell = memo(
    ({
        row,
        table,
    }: CellContext<
        ViewTableFeatures,
        FinancialDocumentListItem,
        DocumentCategory | null
    >) => {
        const document = row.original;
        const updateCategory = useUpdateDocumentCategoryMutation();
        const { isViewOnly, refuseIfViewOnly } = useViewOnlyRefusal();
        const isTableReadonly = useIsFinancialDocumentsTableReadonly(table);
        const documentCategory = document.documentCategory ?? null;
        const isCategorizing =
            document.taxonomyExtractionStatus === ExtractionStatus.IN_PROGRESS;
        const hasValue = documentCategory !== null;

        const handleSelected = useCallback(
            (selected: DocumentCategory | null) => {
                if (refuseIfViewOnly()) {
                    return;
                }
                updateCategory.mutate({
                    document,
                    documentCategory: selected,
                });
            },
            [updateCategory, document, refuseIfViewOnly],
        );

        const triggerLabel = useMemo(() => {
            if (hasValue) {
                return documentCategory.label;
            }
            return isCategorizing ? "Categorizing" : "Select category";
        }, [hasValue, documentCategory, isCategorizing]);

        const triggerContent = useMemo(() => {
            if (hasValue) {
                return (
                    <HStack as="span" align="center" gap={8}>
                        <DocumentCategoryIcon
                            documentTaxonomy={documentCategory.documentTaxonomy}
                        />
                        <BreakableText text={triggerLabel} showTooltip />
                    </HStack>
                );
            }

            return (
                <HStack
                    as="span"
                    gap={8}
                    align="center"
                    className={styles.uncategorizedTrigger}
                >
                    {isCategorizing ? (
                        <ClockIcon className="icon-color-gray-500" />
                    ) : (
                        <DottedCircleIcon className="icon-color-gray-500" />
                    )}
                    {triggerLabel}
                </HStack>
            );
        }, [hasValue, documentCategory, isCategorizing, triggerLabel]);

        const trigger = useMemo(
            () => (
                <Picker.CellContent
                    className={styles.documentCategoryCell}
                    value={triggerContent}
                    aria-label={triggerLabel}
                />
            ),
            [triggerContent, triggerLabel],
        );

        return (
            <ViewTableCell
                captureClicks
                className={styles.documentCategoryCell}
            >
                <DocumentCategoryCombobox
                    selectedCategory={documentCategory}
                    onChange={handleSelected}
                    hideArrow
                    hideFooter
                    readOnly={isViewOnly || isTableReadonly}
                    trigger={trigger}
                />
            </ViewTableCell>
        );
    },
);
DocumentCategoryCell.displayName = "DocumentCategoryCell";
